import Config from 'services/config/config';
import callWebService, { callTypes } from 'services/utils/networks/network';
import { toDisplayDate } from 'pages/darwinRecord/darwinRecordUtils';

export const extractLaravelDarwinData = (res) => {
  if (!res?.data) {
    return { data: null, error: 'No response from server' };
  }
  const body = res.data;
  const inner = body.response || {};
  const innerStatus = inner.status;
  // Laravel V1: nested response.status true/200, or status_code 200.
  // Go V2: status 0 (interceptor also adds response.status=true + status_code 200).
  const isSuccess =
    innerStatus === true ||
    innerStatus === 200 ||
    body.status === 0 ||
    Number(body.status) === 200 ||
    Number(body.status_code) === 200;

  if (isSuccess) {
    const payload = inner.data !== undefined ? inner.data : body.data;
    return {
      data: payload !== undefined ? payload : true,
      error: null,
    };
  }

  const errorMessage =
    inner.message ||
    body.message ||
    (Array.isArray(body.messages) && body.messages.length
      ? body.messages.join(', ')
      : null) ||
    'Request failed';

  return { data: null, error: errorMessage };
};

const normalizeStatus = (status, isSync = '') => {
  if (status === 'N.A.' || status === 'N/A') return 'N/A';
  const heading = status || 'N/A';
  // LeadX uses Status "Regularization Pending". Laravel/Go used to put that
  // only in IsSync while Status stayed Absent.
  if (
    heading === 'Absent' &&
    /regularization pending/i.test(String(isSync))
  ) {
    return 'Regularization Pending';
  }
  return heading;
};

export const serializeDarwinRecordItem = (row) => {
  const rawHolidayName = row.HolidayName ?? row.holiday_name ?? '';
  const holidayName = String(rawHolidayName)
    .replace(/^\s*\(/, '')
    .replace(/\)\s*$/, '')
    .trim();
  const rawIsSync = row.IsSync ?? row.is_sync ?? '';
  const status = normalizeStatus(row.Status ?? row.status, rawIsSync);
  const isSyncStr =
    status === 'Regularization Pending' &&
    /regularization pending/i.test(String(rawIsSync))
      ? ''
      : rawIsSync;

  return {
    day: String(row.Day ?? row.day ?? ''),
    dateStr: row.FormattedDate ?? row.formatted_date ?? row.date ?? '',
    formattedDateStr: row.FormattedDate ?? row.formatted_date ?? '',
    month: row.Month ?? row.month ?? '',
    status,
    holidayName,
    isSyncStr,
    canRegularize: Boolean(row.Regularize ?? row.regularize),
    checkIn: row.Checkin ?? row.check_in ?? '',
    checkOut: row.Checkout ?? row.check_out ?? '',
  };
};

export const parseMonthFilterId = (id) => {
  if (!id) return null;
  const [month, year] = id.split('-');
  if (!month || !year) return null;
  return { month: month.padStart(2, '0'), year };
};

export const getMonthsFilter = async () => {
  const res = await callWebService(
    callTypes.get,
    '',
    Config.darwinAttendance.monthsFilter
  );
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };

  const fields = Array.isArray(data) ? data : [];
  const monthsField = fields.find(
    (field) =>
      field.place_holder?.toLowerCase() === 'months' || Array.isArray(field.values)
  );
  return { data: monthsField?.values ?? [], error: null };
};

export const getDarwinRecordData = async ({ month, year }) => {
  const url = `${Config.darwinAttendance.monthlyAttendance}?month=${Number(month)}&year=${year}`;
  const res = await callWebService(callTypes.get, '', url);
  // GET HTTP 4xx: axios catch toasts the Go message and returns undefined.
  if (!res?.data) {
    return { data: null, error: null };
  }
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };
  if (!Array.isArray(data)) return { data: [], error: null };
  return {
    data: data.map(serializeDarwinRecordItem),
    error: null,
  };
};

const toIsoDate = (date) => {
  const ddmmyyyy = /^(\d{2})-(\d{2})-(\d{4})$/;
  const match = String(date).match(ddmmyyyy);
  if (match) {
    return `${match[3]}-${match[2]}-${match[1]}`;
  }
  return date;
};

const serializeDailyActivity = (row) => ({
  checkInTime: row.check_in_date ?? row.checkInTime ?? '',
  checkOutTime: row.check_out_date ?? row.checkOutTime ?? '',
  officeLocation: row.office_location ?? '',
  regularizationStatus: Number(row.regularization_status ?? 0),
  regularizationRemarks: row.regularization_approver_remark ?? '',
  moduleType: row.module_type ?? '',
  subject: row.subject ?? '',
  body: row.body ?? '',
});

const serializeDailyRegularization = (row) => ({
  regularizationDate: row.regularization_date ?? row.regularizationDate ?? '',
  checkInTime: row.check_in_time ?? row.checkInTime ?? '',
  checkOutTime: row.check_out_time ?? row.checkOutTime ?? '',
  reasonForMissedAttendance:
    row.reason_for_missed_attendance ?? row.reasonForMissedAttendance ?? '',
  message: row.message ?? '',
  status: Number(row.status ?? row.regularization_status ?? 0),
});

export const getDarwinDailyMeetings = async ({ date }) => {
  const isoDate = toIsoDate(date);
  const url = `${Config.darwinAttendance.dailyMeetings}?date=${encodeURIComponent(isoDate)}`;
  const res = await callWebService(callTypes.get, '', url);
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };

  const rows = Array.isArray(data) ? data : [];
  return {
    data: {
      activities: rows
        .filter((item) => !!item.module_type)
        .map(serializeDailyActivity),
      regularization: rows
        .filter((item) => !!item.regularization_date || !!item.check_in_time)
        .filter((item) => !item.module_type)
        .map(serializeDailyRegularization),
    },
    error: null,
  };
};

export const createRegularization = async (payload) => {
  const laravelPayload = {
    reason_for_missed_attendance: payload.reason_for_missed_attendance,
    location_of_work: payload.location_of_work,
    regularization_date: toDisplayDate(payload.regularization_date),
    check_in_time: payload.check_in_time,
    check_out_time: payload.check_out_time,
    message: payload.message,
    attachment: payload.attachment || '',
    regularization_mode: payload.regularization_mode ?? 0,
  };

  const res = await callWebService(
    callTypes.post,
    laravelPayload,
    Config.darwinAttendance.createRegularization
  );
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };
  return { data: data ?? true, error: null };
};

const serializeRegularizationRequest = (row) => ({
  requestorName:
    row.requestor_name ?? row.requester_name ?? row.employee_name ?? '',
  requestorEmail:
    row.requestor_email ?? row.requester_email ?? row.email_id ?? '',
  type: row.regularization_type ?? row.type ?? 'REGULARIZATION',
  requestedForDate: row.regularization_date ?? row.requested_for_date ?? '',
  requestedOnDate: row.requested_date ?? row.created_at ?? '',
  status: Number(row.regularization_status ?? row.status ?? 0),
  id: Number(row.id),
});

const buildRegularizationListParams = (payload) => {
  const params = new URLSearchParams();
  params.set('page', String(payload.page ?? 1));
  params.set('limit', String(payload.limit ?? 10));
  params.set('list_status', payload.list_status ?? '0');

  if (payload.request_type) params.set('request_type', payload.request_type);
  if (payload.requester) params.set('requester', payload.requester);

  const requestorEmail =
    payload.requestor_email ?? payload.requester_email ?? null;
  if (requestorEmail) {
    params.set('requestor_email', requestorEmail);
  }

  if (payload.start_date) params.set('start_date', payload.start_date);
  if (payload.end_date) params.set('end_date', payload.end_date);

  return params;
};

export const getRegularizationRequests = async (payload) => {
  const params = buildRegularizationListParams(payload);
  const url = `${Config.darwinAttendance.regularizationRequests}?${params.toString()}`;
  const res = await callWebService(callTypes.get, '', url);
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };

  const paginator = data;
  const rawResults = Array.isArray(paginator)
    ? paginator
    : paginator?.data ?? paginator?.results ?? [];
  const count = Array.isArray(paginator)
    ? rawResults.length
    : Number(paginator?.total ?? paginator?.count ?? rawResults.length ?? 0);

  return {
    data: {
      results: rawResults.map(serializeRegularizationRequest),
      count,
      perPage: Number(paginator?.per_page ?? payload.limit ?? 10),
      currentPage: Number(paginator?.current_page ?? payload.page ?? 1),
    },
    error: null,
  };
};

export const getRegularizationFilter = async () => {
  const res = await callWebService(
    callTypes.get,
    '',
    Config.darwinAttendance.regularizationFilter
  );
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };
  return { data: Array.isArray(data) ? data : [], error: null };
};

const serializeRegularizationDetails = (row) => ({
  id: Number(row.id),
  requestorName:
    row.requestor_name ?? row.requester_name ?? row.employee_name ?? '',
  requestorEmail:
    row.requestor_email ?? row.requester_email ?? row.email_id ?? '',
  type: row.regularization_type ?? row.type ?? 'REGULARIZATION',
  requestedForDate: row.regularization_date ?? row.check_in_date ?? '',
  requestedOnDate: row.created_at ?? row.requested_date ?? '',
  status: Number(row.regularization_status ?? row.status ?? 0),
  checkInTime: row.check_in_time ?? row.check_in_date ?? '',
  checkOutTime: row.check_out_time ?? row.check_out_date ?? '',
  checkInMessage: row.check_in_comments ?? row.message ?? '',
  checkOutMessage: row.check_out_comments ?? '',
  checkInRemark:
    row.check_in_remark ?? row.reason_for_missed_attendance ?? '',
  checkOutRemark: row.checkout_remark ?? '',
  reasonForMissedAttendance: row.reason_for_missed_attendance ?? '',
  locationOfWork: row.location_of_work ?? '',
  message: row.message ?? row.check_in_comments ?? '',
  attachment: row.attachment ?? '',
  managerRemark: row.manager_remark ?? row.regularization_approver_remark ?? '',
  checkInDate: row.check_in_date ?? '',
  checkOutDate: row.check_out_date ?? '',
  officeLocation: row.office_location ?? '',
});

export const getRegularizationRequestDetails = async ({
  id,
  request_type,
  list_status,
}) => {
  const params = new URLSearchParams();
  params.set('request_id', String(id));
  params.set('request_type', request_type || 'REGULARIZATION');
  params.set('list_status', list_status || '0');

  const url = `${Config.darwinAttendance.regularizationDetails}?${params.toString()}`;
  const res = await callWebService(callTypes.get, '', url);
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };

  const paginator = data;
  const row = Array.isArray(paginator)
    ? paginator[0]
    : Array.isArray(paginator?.data)
      ? paginator.data[0]
      : paginator?.results?.[0] ?? paginator;

  if (
    !row ||
    (row.id == null &&
      !row.requestor_name &&
      !row.requester_name &&
      !row.employee_name)
  ) {
    return { data: null, error: 'Unable to fetch request details' };
  }

  return { data: serializeRegularizationDetails(row), error: null };
};

export const updateRegularizationRequest = async (payload) => {
  const res = await callWebService(
    callTypes.patch,
    {
      id: payload.id,
      type: payload.type || 'REGULARIZATION',
      status: payload.status,
      manager_remark: payload.manager_remark,
    },
    Config.darwinAttendance.updateRegularization
  );
  const { data, error } = extractLaravelDarwinData(res);
  if (error) return { data: null, error };
  return { data: data ?? true, error: null };
};
